// Mirrors the response shape of community-admin's
// GET /api/public/profile/:username/community-signals.
// Kept in a non-`.server` module so both the server loader/client and the
// client-rendered card can import it.

export interface CommunityAttendanceSignal {
  count: number;
  recent: {
    title: string;
    city: string | null;
    date: string;
    eventSlug?: string | null;
  }[];
}

export interface CommunityQuestionsSignal {
  count: number;
  upvotes: number;
}

export interface CommunityConnectionsSignal {
  count: number;
}

export interface CommunityTalkTestimonial {
  quote: string;
  authorName?: string;
  attribution: "named" | "anonymous";
  date: string;
}

export interface CommunityTalkItem {
  eventId: number;
  talkId: number;
  title: string;
  eventName: string;
  city: string | null;
  date: string;
  eventSlug?: string | null;
  recordingUrl?: string;
  avgRating?: number;
  testimonials?: CommunityTalkTestimonial[];
}

export interface CommunityTalksSignal {
  count: number;
  items: CommunityTalkItem[];
}

export interface CommunityTestimonialsSignal {
  count: number;
  items: CommunityTalkTestimonial[];
}

export interface CommunityVerifiedLearningItem {
  talkTitle: string;
  eventName: string;
  topics?: string[];
  date: string;
}

export interface CommunityVerifiedLearningSignal {
  count: number;
  items: CommunityVerifiedLearningItem[];
}

// Deliberately count-only: hosted events arrive through `activity` items
// (roles include "hosted"), so no recent-events list is mirrored here.
export interface CommunityHostingSignal {
  count: number;
  hubTeam?: {
    cities: string[];
  };
}

export type CommunityActivityRole = "spoke" | "hosted" | "attended";

export interface CommunityActivityItem {
  eventSlug: string | null;
  eventName: string;
  date: string;
  city: string | null;
  roles: CommunityActivityRole[];
}

export interface CommunityActivitySignal {
  participationCount: number;
  items: CommunityActivityItem[];
}

export interface CommunitySignals {
  attendance?: CommunityAttendanceSignal;
  questions?: CommunityQuestionsSignal;
  connections?: CommunityConnectionsSignal;
  talks?: CommunityTalksSignal;
  testimonials?: CommunityTestimonialsSignal;
  verifiedLearning?: CommunityVerifiedLearningSignal;
  hosting?: CommunityHostingSignal;
  activity?: CommunityActivitySignal;
}

export function hasAnyCommunitySignal(
  signals: CommunitySignals | null | undefined,
): signals is CommunitySignals {
  if (!signals) return false;
  // `testimonials` is intentionally omitted: it is always scoped to (and
  // rendered under) `talks`, so `talks` already covers its presence.
  return Boolean(
    signals.attendance ||
      signals.verifiedLearning ||
      signals.questions ||
      signals.connections ||
      signals.talks ||
      signals.hosting ||
      signals.activity,
  );
}
